import { GridColDef, GridColumnDimensions, GridInitialState, GridPinnedColumnFields } from '@mui/x-data-grid-pro'

/** MUI row-selection checkbox column — always frozen on the left. */
export const INVENTORY_V3_CHECKBOX_COLUMN = '__check__'

/** Header filter row: singleSelect columns use dropdown only (no operator menu). */
export const INVENTORY_V3_SINGLE_SELECT_HEADER_FILTER_CLASS = 'inventory-v3-single-select-filter'

export const ensureCheckboxPinnedLeft = (
  pinnedColumns: GridPinnedColumnFields,
): GridPinnedColumnFields => {
  const left = (pinnedColumns.left ?? []).filter(field => field !== INVENTORY_V3_CHECKBOX_COLUMN)
  return {
    left: [INVENTORY_V3_CHECKBOX_COLUMN, ...left],
    right: pinnedColumns.right ?? [],
  }
}

export const DEFAULT_V3_TEMPLATE_ID = 'default'

/**
 * Column order / widths from the AG Grid "Default View" preset
 * (`COLUMN_TEMPLATES` id `default`), mapped onto Inventory v3 field names.
 * `vehicleIcon` → `edit`. `shipping` (Timeline) → `timeline`.
 * `sold_*_user.name` → `sold_*_name`. `pickup_address` is Complete/Incomplete/N/A from pickup_location (N/A if refinance).
 */
export const DEFAULT_V3_COLUMN_ORDER = [
  'edit',
  'photos',
  'documents',
  'title_state',
  'timeline',
  'delivered_to_customer',
  'mechanical_status',
  'body_status',
  'safety',
  'type',
  'stock_number',
  'who',
  'condition',
  'tier',
  'year',
  'make',
  'model',
  'size',
  'color',
  'miles',
  'seller_asking_price',
  'optic_list_price',
  'profit',
  'profit_realized',
  'vin',
  'link',
  'pickup_address',
  'ready_for_transport',
  'refinance',
  'favorite',
  'paid_for',
  'ready_for_sale',
  'funded',
  'id',
  'date_added',
  'fuel',
  'location',
  'sold_date',
  'truck_notes',
  'phone_number',
  'email',
  'follow_up_notes',
  'entered_by',
  'lien_payoff_amount',
  'repair_cost',
  'estimated_repair_cost',
  'shipping_cost',
  'show_on_website',
  'status',
  'sold_to_name',
  'sold_by_name',
  'selling_to_name',
  'bought_from',
  'size_confirmed',
  'cameras',
  'shelves',
  'rear_wheel',
] as const

export const DEFAULT_V3_COLUMN_WIDTHS: Record<string, number> = {
  edit: 80,
  photos: 80,
  documents: 150,
  title_state: 120,
  timeline: 80,
  delivered_to_customer: 110,
  mechanical_status: 160,
  body_status: 160,
  safety: 180,
  type: 130,
  stock_number: 188,
  who: 162,
  condition: 161,
  tier: 120,
  year: 128,
  make: 149,
  model: 155,
  size: 173,
  color: 130,
  miles: 133,
  seller_asking_price: 217,
  optic_list_price: 195,
  profit: 137,
  profit_realized: 160,
  vin: 217,
  link: 126,
  pickup_address: 150,
  ready_for_transport: 180,
  refinance: 90,
  favorite: 100,
  paid_for: 172,
  ready_for_sale: 150,
  funded: 120,
  id: 200,
  date_added: 220,
  fuel: 200,
  location: 154,
  sold_date: 220,
  truck_notes: 175,
  phone_number: 192,
  email: 166,
  follow_up_notes: 200,
  entered_by: 178,
  lien_payoff_amount: 194,
  repair_cost: 172,
  estimated_repair_cost: 172,
  shipping_cost: 185,
  show_on_website: 172,
  status: 169,
  sold_to_name: 150,
  sold_by_name: 150,
  selling_to_name: 150,
  bought_from: 150,
  size_confirmed: 130,
  cameras: 130,
  shelves: 130,
  rear_wheel: 130,
}

export const DEFAULT_V3_COLUMN_DIMENSIONS: Record<string, GridColumnDimensions> = Object.fromEntries(
  Object.entries(DEFAULT_V3_COLUMN_WIDTHS).map(([field, width]) => [field, { width }]),
)

/** Templates may omit widths; fill gaps from the default preset. */
export const mergeColumnDimensions = (
  dimensions?: Record<string, GridColumnDimensions>,
): Record<string, GridColumnDimensions> => ({
  ...DEFAULT_V3_COLUMN_DIMENSIONS,
  ...(dimensions ?? {}),
})

export const applyColumnDimensions = <T extends GridColDef>(
  columns: T[],
  dimensions: Record<string, GridColumnDimensions>,
): T[] =>
  columns.map(column => {
    const width = dimensions[column.field]?.width
    return width != null ? { ...column, width } : column
  })

const isInternalColumnField = (field: string) => field.startsWith('__')

/** Templates may omit fields added later; keep saved order and append the rest. */
export const mergeColumnOrder = (orderedFields?: readonly string[]): string[] => {
  const merged: string[] = []
  const seen = new Set<string>()
  for (const field of orderedFields ?? []) {
    if (isInternalColumnField(field) || seen.has(field)) continue
    seen.add(field)
    merged.push(field)
  }
  for (const field of DEFAULT_V3_COLUMN_ORDER) {
    if (seen.has(field)) continue
    seen.add(field)
    merged.push(field)
  }
  return merged
}

export const applyColumnOrder = <T extends GridColDef>(
  columns: T[],
  orderedFields: readonly string[],
): T[] => {
  const rank = new Map(orderedFields.map((field, index) => [field, index]))
  return columns.toSorted((left, right) => {
    const leftRank = rank.get(left.field) ?? orderedFields.length
    const rightRank = rank.get(right.field) ?? orderedFields.length
    return leftRank - rightRank
  })
}

/** Hidden in the AG Grid default preset, plus v3-only columns that are not in that preset. */
export const DEFAULT_V3_HIDDEN_FIELDS = [
  'id',
  'fuel',
  'follow_up_notes',
  'grayBookPrice',
  'ltv',
  'dealQuality',
  'date_available',
  'gwvr_over_10k',
  '__detail_panel_toggle__',
] as const

const DEFAULT_VISIBLE = new Set<string>(
  DEFAULT_V3_COLUMN_ORDER.filter(field => !(DEFAULT_V3_HIDDEN_FIELDS as readonly string[]).includes(field)),
)

export const DEFAULT_V3_COLUMN_VISIBILITY: Record<string, boolean> = {
  ...Object.fromEntries(DEFAULT_V3_COLUMN_ORDER.map(field => [field, DEFAULT_VISIBLE.has(field)])),
  ...Object.fromEntries(DEFAULT_V3_HIDDEN_FIELDS.map(field => [field, false])),
}

export const DEFAULT_V3_GRID_STATE: GridInitialState = {
  pinnedColumns: ensureCheckboxPinnedLeft({ left: [], right: [] }),
  sorting: {
    sortModel: [{ field: 'date_added', sort: 'desc' }],
  },
  columns: {
    orderedFields: [...DEFAULT_V3_COLUMN_ORDER],
    columnVisibilityModel: DEFAULT_V3_COLUMN_VISIBILITY,
    dimensions: DEFAULT_V3_COLUMN_DIMENSIONS,
  },
}
